import React from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { Cancel01Icon, Copy01Icon, Link01Icon, Tick02Icon } from '@hugeicons/core-free-icons';
import Select from 'react-select';
import Modal from '../../shared/components/core-ui/modal/modal.component';
import { WEBSITE_HOME } from '../../shared/config/routes-config';
import UserMapper from '../../shared/mappers/user.mapper';
import api from '../../shared/services/utils/api/api.service';

const salesPersonSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: 48,
    borderRadius: 10,
    borderColor: state.isFocused ? '#207150' : '#e5e7eb',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(32, 113, 80, 0.15)' : 'none',
    ':hover': { borderColor: state.isFocused ? '#207150' : '#d1d5db' }
  }),
  valueContainer: (base: any) => ({ ...base, padding: '0 12px' }),
  placeholder: (base: any) => ({ ...base, color: '#9ca3af' }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected ? '#207150' : state.isFocused ? '#e9f2ee' : 'white',
    color: state.isSelected ? 'white' : '#1f2937',
    cursor: 'pointer'
  }),
  menu: (base: any) => ({ ...base, borderRadius: 10, overflow: 'hidden' }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 })
};

type SalesPersonOption = { value: string; label: string };

export function buildPromoLink(code: string, salesPersonId?: string) {
  const url = new URL(WEBSITE_HOME);
  url.searchParams.set('promo', code);
  if (salesPersonId) url.searchParams.set('sp', salesPersonId);
  return url.toString();
}

export function PromoLinkModal({ code, onClose }: { code: string; onClose: () => void }) {
  const [withSalesPerson, setWithSalesPerson] = React.useState(false);
  const [salesPersonOptions, setSalesPersonOptions] = React.useState<SalesPersonOption[] | null>(
    null
  );
  const [salesPerson, setSalesPerson] = React.useState<SalesPersonOption | null>(null);
  const [loadError, setLoadError] = React.useState('');
  const [isCopied, setIsCopied] = React.useState(false);

  const link = buildPromoLink(code, withSalesPerson ? salesPerson?.value : undefined);
  const canCopy = !withSalesPerson || Boolean(salesPerson);

  function handleToggleSalesPerson(checked: boolean) {
    setWithSalesPerson(checked);
    setIsCopied(false);
    if (!checked || salesPersonOptions) return;

    setLoadError('');
    api
      .getSalesPersons()
      .then((response) => {
        if (!response.ok || !Array.isArray(response.data?.data)) {
          setLoadError('Sales persons could not be loaded.');
          return;
        }
        setSalesPersonOptions(
          UserMapper.mapToUIList(response.data!.data)
            .filter((item) => item.id)
            .map((item) => ({
              value: String(item.id),
              label: item.name || item.email || 'Unnamed sales person'
            }))
        );
      })
      .catch(() => setLoadError('Sales persons could not be loaded.'));
  }

  function handleCopy() {
    navigator.clipboard
      .writeText(link)
      .then(() => setIsCopied(true))
      .catch(() => setIsCopied(false));
  }

  React.useEffect(() => {
    if (!isCopied) return;
    const timeout = window.setTimeout(() => setIsCopied(false), 2000);
    return () => window.clearTimeout(timeout);
  }, [isCopied]);

  return (
    <Modal
      config={{
        showModal: true,
        fullScreen: false,
        showClose: false,
        maxWidth: 'sm'
      }}
      eventHandlers={{
        closeModalHandler: onClose
      }}
    >
      <div className="w-full rounded-2xl bg-white p-6 md:p-7">
        <div className="mb-6 flex items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <h2 className="text-xl font-gilroy-bold text-neutral-900">Share promo code</h2>
            <span className="truncate rounded-full bg-primary-50 px-3 py-1 text-sm font-gilroy-bold text-primary-700">
              {code}
            </span>
          </div>
          <button
            type="button"
            aria-label="Close promo code link"
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-neutral-500 transition hover:bg-gray-100 hover:text-neutral-800 focus:outline-none focus:ring-2 focus:ring-primary-500"
            onClick={onClose}
          >
            <HugeiconsIcon icon={Cancel01Icon} size={22} />
          </button>
        </div>

        <div className="mb-5 rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between gap-4">
            <span className="text-base font-gilroy-medium text-neutral-800">Add salesperson</span>
            <button
              type="button"
              role="switch"
              aria-checked={withSalesPerson}
              aria-label="Add salesperson"
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 ${
                withSalesPerson ? 'bg-primary-600' : 'bg-gray-300'
              }`}
              onClick={() => handleToggleSalesPerson(!withSalesPerson)}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                  withSalesPerson ? 'translate-x-[22px]' : 'translate-x-[2px]'
                }`}
              />
            </button>
          </div>

          {withSalesPerson ? (
            <div className="mt-4">
              <Select
                isClearable
                autoFocus
                placeholder="Select salesperson"
                isLoading={!salesPersonOptions && !loadError}
                options={salesPersonOptions || []}
                value={salesPerson}
                styles={salesPersonSelectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                onChange={(option) => {
                  setSalesPerson((option as SalesPersonOption) || null);
                  setIsCopied(false);
                }}
              />
              {loadError ? <p className="mt-2 text-sm text-red-500">{loadError}</p> : null}
            </div>
          ) : null}
        </div>

        <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 p-2 pl-4">
          <HugeiconsIcon icon={Link01Icon} size={20} className="shrink-0 text-neutral-400" />
          <span
            className={`min-w-0 flex-1 truncate text-sm ${
              canCopy ? 'text-neutral-700' : 'text-neutral-400'
            }`}
            title={canCopy ? link : undefined}
          >
            {canCopy ? link : 'Select a salesperson'}
          </span>
          <button
            type="button"
            disabled={!canCopy}
            className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-base font-gilroy-medium text-white transition hover:bg-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-300"
            onClick={handleCopy}
          >
            <HugeiconsIcon icon={isCopied ? Tick02Icon : Copy01Icon} size={18} />
            {isCopied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
